import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "@src/API.js";
import BreadCrumb from "../Controls/BreadCrumb";
import CustomButton from "../Controls/CustomButton";
import Helpers from "../Controls/helpers";
import { toast } from "react-toastify";
import { usePersonSkillsQuery } from "../../query/usePersonSkills";
import { useSessionShellQuery } from "../../query/useSessionShell";
import { openSkillForum } from "../../skillForum/openForum";
import { canonicalSkillId, personSkillDisplayName } from "../ListItems/PersonSkillListItem";
import "../../style/scss/SkillForum.scss";

const LEVEL_NAMES = {
  1: "Beginner",
  2: "Junior",
  3: "Intermediate",
  4: "Senior",
  5: "Guru",
};

const emptyQuestion = () => ({
  skillLevelID: 1,
  questionText: "",
  alternativeToQuestionID: null,
  answers: [
    { text: "", correctAnswer: true },
    { text: "", correctAnswer: false },
  ],
});

const idOf = (row) => Number(row?.ID || row?.id) || 0;
const personIdOf = (row) => Number(row?.PersonID || row?.personID) || 0;
const statusCode = (question) =>
  Number(question?.SkillQuestionStatus ?? question?.skillQuestionStatus ?? -1);
const isPending = (question) => statusCode(question) === 0;
const isApproved = (question) => statusCode(question) === 1;
const isContested = (question) => statusCode(question) === 4;
const isVotable = (question) => {
  const s = statusCode(question);
  return s === 0 || s === 1 || s === 4;
};

const STATUS_LABELS = {
  0: "Pending",
  1: "Approved",
  2: "Rejected",
  3: "Duplicate",
  4: "Contested",
};

const questionStatusLabel = (question) => {
  const named = question?.Status || question?.status;
  if (named) return named;
  const code = question?.SkillQuestionStatus ?? question?.skillQuestionStatus;
  if (code === undefined || code === null) return "Pending";
  return STATUS_LABELS[code] || String(code);
};

export default function SkillForum() {
  const { skillId: skillIdParam } = useParams();
  const navigate = useNavigate();
  const requestedSkillId = Number(skillIdParam) || 0;
  const { data: skillData } = usePersonSkillsQuery();
  const skills = skillData?.skills || [];
  const personSkills = skillData?.personSkills || [];
  const catalogSkill = skills.find((skill) => idOf(skill) === requestedSkillId);
  const skillId = canonicalSkillId(catalogSkill, { SkillID: requestedSkillId });
  const personSkill = personSkills.find(
    (row) => canonicalSkillId(
      skills.find((skill) => idOf(skill) === Number(row?.SkillID || row?.skillID)),
      row
    ) === skillId
  );
  const skillName = personSkillDisplayName(catalogSkill, personSkill) || `Skill ${skillId}`;
  const { data: shell } = useSessionShellQuery();
  const currentPersonId = Number(
    shell?.person?.ID ||
    shell?.person?.id ||
    window.sessionStorage.getItem("UserId")
  ) || 0;
  const isSysAdmin = Helpers.isSysAdmin(shell || {});

  const [questions, setQuestions] = useState([]);
  const [articles, setArticles] = useState([]);
  const [eligible, setEligible] = useState(null);
  const [moderators, setModerators] = useState([]);
  const [addModPersonId, setAddModPersonId] = useState("");
  const [form, setForm] = useState(emptyQuestion);
  const [articleForm, setArticleForm] = useState({ subject: "", body: "", skillLevelID: 3, questionIds: [] });
  const [selectedQuestionId, setSelectedQuestionId] = useState(0);
  const [discussion, setDiscussion] = useState([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [editSuggestions, setEditSuggestions] = useState([]);
  const [editForm, setEditForm] = useState(null);

  const verifiedLevel = Number(personSkill?.VerifiedSkillLevel ?? personSkill?.verifiedSkillLevel) || 0;
  const isIntermediatePlus = verifiedLevel >= 3;

  const selectedQuestion = useMemo(
    () => questions.find((question) => idOf(question) === selectedQuestionId) || null,
    [questions, selectedQuestionId]
  );

  const isModerator = useMemo(
    () => isSysAdmin || moderators.some((mod) => personIdOf(mod) === currentPersonId),
    [isSysAdmin, moderators, currentPersonId]
  );

  useEffect(() => {
    if (requestedSkillId > 0 && skillId > 0 && requestedSkillId !== skillId) {
      navigate(`/skillForum/${skillId}`, { replace: true });
    }
  }, [navigate, requestedSkillId, skillId]);

  useEffect(() => {
    if (skillId > 0) openSkillForum({ skillId, skillName });
  }, [skillId, skillName]);

  useEffect(() => {
    if (!(skillId > 0)) {
      setQuestions([]);
      setArticles([]);
      setEligible(null);
      setModerators([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const [questionsResp, eligibleResp, modsResp, articlesResp] = await Promise.all([
        API.getAsync(`/Skill/Question?SkillID=${skillId}`),
        API.getAsync(`/Skill/Test/Eligible?SkillID=${skillId}`),
        API.getAsync(`/Skill/${skillId}/Moderators`),
        API.getAsync(`/Skill/${skillId}/Articles`),
      ]);
      if (cancelled) return;
      if (questionsResp?.ok) setQuestions(questionsResp.jsonData || []);
      if (eligibleResp?.ok) setEligible(eligibleResp.jsonData || null);
      if (modsResp?.ok) setModerators(modsResp.jsonData || []);
      if (articlesResp?.ok) setArticles(articlesResp.jsonData || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [skillId]);

  useEffect(() => {
    if (!selectedQuestion || !isPending(selectedQuestion)) {
      setDiscussion([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const response = await API.getAsync(`/Skill/Question/${selectedQuestionId}/Discussion`);
      if (!cancelled && response?.ok) setDiscussion(response.jsonData || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedQuestion, selectedQuestionId]);

  useEffect(() => {
    if (!(selectedQuestionId > 0)) {
      setEditSuggestions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const response = await API.getAsync(`/Skill/Question/${selectedQuestionId}/EditSuggestions`);
      if (!cancelled && response?.ok) setEditSuggestions(response.jsonData || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedQuestionId]);

  async function refreshQuestions() {
    const response = await API.getAsync(`/Skill/Question?SkillID=${skillId}`);
    if (response?.ok) setQuestions(response.jsonData || []);
  }

  async function refreshModerators() {
    const response = await API.getAsync(`/Skill/${skillId}/Moderators`);
    if (response?.ok) setModerators(response.jsonData || []);
  }

  async function refreshArticles() {
    const response = await API.getAsync(`/Skill/${skillId}/Articles`);
    if (response?.ok) setArticles(response.jsonData || []);
  }

  async function createArticle(event) {
    event.preventDefault();
    if (busy || !(skillId > 0)) return;
    setBusy(true);
    try {
      const response = await API.postAsync(`/Skill/${skillId}/Articles`, {
        subject: articleForm.subject,
        body: articleForm.body,
        skillLevelID: articleForm.skillLevelID,
        questionIds: articleForm.questionIds,
      });
      if (!response?.ok) {
        toast.error(response?.jsonData?.message || "Could not submit article");
        return;
      }
      toast.success("Article submitted (Pending)");
      setArticleForm({ subject: "", body: "", skillLevelID: 3, questionIds: [] });
      await refreshArticles();
    } finally {
      setBusy(false);
    }
  }

  async function approveArticle(articleId) {
    const response = await API.postAsync(`/Skill/Article/${articleId}/Approve`, {});
    if (!response?.ok) {
      toast.error(response?.jsonData?.message || "Could not approve article");
      return;
    }
    toast.success(response.jsonData?.status === "Approved" ? "Article published" : "Approval recorded");
    await refreshArticles();
  }

  async function rejectArticle(articleId) {
    const response = await API.postAsync(`/Skill/Article/${articleId}/Reject`, {});
    if (!response?.ok) {
      toast.error(response?.jsonData?.message || "Could not reject article");
      return;
    }
    toast.success("Article rejected");
    await refreshArticles();
  }

  function updateAnswer(index, patch) {
    setForm((current) => {
      const answers = current.answers.map((answer, answerIndex) =>
        answerIndex === index ? { ...answer, ...patch } : answer
      );
      if (patch.correctAnswer) {
        answers.forEach((answer, answerIndex) => {
          if (answerIndex !== index) answer.correctAnswer = false;
        });
      }
      return { ...current, answers };
    });
  }

  async function createQuestion(event) {
    event.preventDefault();
    if (busy || !(skillId > 0)) return;
    setBusy(true);
    try {
      const response = await API.postAsync("/Skill/Question", {
        ...form,
        skillID: skillId,
        alternativeToQuestionID: form.alternativeToQuestionID || null,
        answers: form.answers.filter((answer) => answer.text.trim()),
      });
      if (!response?.ok) {
        toast.error(response?.jsonData?.message || "Could not submit question");
        return;
      }
      toast.success("Question submitted (Pending)");
      setForm(emptyQuestion());
      await refreshQuestions();
    } finally {
      setBusy(false);
    }
  }

  async function approveQuestion(questionId) {
    const response = await API.postAsync(`/Skill/Question/${questionId}/Approve`, {});
    if (!response?.ok) {
      toast.error(response?.jsonData?.message || "Could not record approval");
      return;
    }
    toast.success(response.jsonData?.status === "Approved" ? "Question approved" : "Approval recorded");
    await refreshQuestions();
  }

  async function disapproveQuestion(questionId) {
    const response = await API.postAsync(`/Skill/Question/${questionId}/Disapprove`, {});
    if (!response?.ok) {
      toast.error(response?.jsonData?.message || "Could not record disapproval");
      return;
    }
    toast.success(
      response.jsonData?.status === "Contested" ? "Question contested" : "Disapproval recorded"
    );
    await refreshQuestions();
  }

  async function restoreQuestion(questionId) {
    const response = await API.postAsync(`/Skill/Question/${questionId}/Restore`, {});
    if (!response?.ok) {
      toast.error(response?.jsonData?.message || "Could not restore question");
      return;
    }
    toast.success("Question restored from contested");
    await refreshQuestions();
  }

  async function submitEditSuggestion(event) {
    event.preventDefault();
    if (!editForm || !(selectedQuestionId > 0) || busy) return;
    setBusy(true);
    try {
      const response = await API.postAsync(`/Skill/Question/${selectedQuestionId}/EditSuggestion`, {
        questionText: editForm.questionText,
        explanation: editForm.explanation || "",
        answers: editForm.answers.filter((a) => a.text.trim()),
      });
      if (!response?.ok) {
        toast.error(response?.jsonData?.message || "Could not submit suggestion");
        return;
      }
      toast.success("Edit suggestion submitted");
      setEditForm(null);
      const list = await API.getAsync(`/Skill/Question/${selectedQuestionId}/EditSuggestions`);
      if (list?.ok) setEditSuggestions(list.jsonData || []);
    } finally {
      setBusy(false);
    }
  }

  async function resolveEditSuggestion(suggestionId, accept) {
    const path = accept ? "Accept" : "Reject";
    const response = await API.postAsync(`/Skill/Question/EditSuggestion/${suggestionId}/${path}`, {});
    if (!response?.ok) {
      toast.error(response?.jsonData?.message || `Could not ${path.toLowerCase()} suggestion`);
      return;
    }
    toast.success(accept ? "Suggestion applied" : "Suggestion rejected");
    await refreshQuestions();
    const list = await API.getAsync(`/Skill/Question/${selectedQuestionId}/EditSuggestions`);
    if (list?.ok) setEditSuggestions(list.jsonData || []);
  }

  async function rejectQuestion(questionId, retract) {
    const response = await API.postAsync(`/Skill/Question/${questionId}/Reject`, {});
    if (!response?.ok) {
      toast.error(response?.jsonData?.message || (retract ? "Could not retract question" : "Could not reject question"));
      return;
    }
    toast.success(retract ? "Question retracted from bank" : "Question rejected");
    await refreshQuestions();
  }

  async function addModerator(event) {
    event.preventDefault();
    const personID = Number(addModPersonId) || 0;
    if (!(personID > 0) || busy) return;
    setBusy(true);
    try {
      const response = await API.postAsync(`/Skill/${skillId}/Moderators`, { personID });
      if (!response?.ok) {
        toast.error(response?.jsonData?.message || "Could not add moderator");
        return;
      }
      toast.success("Moderator added");
      setAddModPersonId("");
      await refreshModerators();
    } finally {
      setBusy(false);
    }
  }

  async function removeModerator(personID) {
    const response = await API.deleteAsync(`/Skill/${skillId}/Moderators/${personID}`);
    if (!response?.ok) {
      toast.error(response?.jsonData?.message || "Could not remove moderator");
      return;
    }
    toast.success("Moderator removed");
    await refreshModerators();
  }

  async function deleteDiscussion(discussionId) {
    const response = await API.deleteAsync(
      `/Skill/Question/${selectedQuestionId}/Discussion/${discussionId}`
    );
    if (!response?.ok) {
      toast.error(response?.jsonData?.message || "Could not remove discussion");
      return;
    }
    toast.success("Discussion removed");
    const listResponse = await API.getAsync(`/Skill/Question/${selectedQuestionId}/Discussion`);
    if (listResponse?.ok) setDiscussion(listResponse.jsonData || []);
  }

  async function startTest(mode, skillLevelID = 0, writeVerification = false) {
    if (busy) return;
    setBusy(true);
    try {
      const response = await API.postAsync("/Skill/Test/Start", {
        skillID: skillId,
        mode,
        skillLevelID,
        writeVerification: !!writeVerification,
      });
      const testId = Number(response?.jsonData?.testId || response?.jsonData?.TestID) || 0;
      if (response?.ok && testId > 0) {
        navigate(`/skillTest/${testId}`);
      } else {
        toast.error(response?.jsonData?.message || "Could not start test");
      }
    } finally {
      setBusy(false);
    }
  }

  async function postDiscussion(event) {
    event.preventDefault();
    if (!selectedQuestionId || !message.trim() || busy) return;
    setBusy(true);
    try {
      const response = await API.postAsync(`/Skill/Question/${selectedQuestionId}/Discussion`, {
        message: message.trim(),
      });
      if (!response?.ok) {
        toast.error(response?.jsonData?.message || "Could not post discussion");
        return;
      }
      setMessage("");
      const listResponse = await API.getAsync(`/Skill/Question/${selectedQuestionId}/Discussion`);
      if (listResponse?.ok) setDiscussion(listResponse.jsonData || []);
    } finally {
      setBusy(false);
    }
  }

  if (!(requestedSkillId > 0)) {
    return <p className="pagebody">Choose a skill from the Skills page to open its forum.</p>;
  }

  return (
    <div className="pagebody skillForumPage">
      <BreadCrumb props={[{ route: "/skills", label: "Skills" }, { label: "Skill Forum" }]} />
      <div className="tabbody">
        <h1>Skill Forum: {skillName}</h1>
        <p>Build practical questions, discuss pending submissions, and validate your proficiency.</p>

        <section className="skillForumSection">
          <h2>Moderators</h2>
          {moderators.length ? (
            <ul className="skillForumModerators">
              {moderators.map((mod) => {
                const modPersonId = personIdOf(mod);
                return (
                  <li key={modPersonId}>
                    <span>{mod.PersonName || mod.personName || `Person ${modPersonId}`}</span>
                    {isModerator ? (
                      <CustomButton
                        type="button"
                        variant="contained"
                        color="primary"
                        disabled={busy}
                        onClick={() => removeModerator(modPersonId)}
                      >
                        Remove
                      </CustomButton>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="fieldHint">No moderators listed yet for this skill.</p>
          )}
          {isModerator ? (
            <form className="skillForumAddMod" onSubmit={addModerator}>
              <label>
                Add moderator (person ID of a skill holder)
                <input
                  value={addModPersonId}
                  onChange={(event) => setAddModPersonId(event.target.value)}
                  inputMode="numeric"
                  required
                />
              </label>
              <CustomButton type="submit" variant="contained" color="primary" disabled={busy}>
                Add moderator
              </CustomButton>
            </form>
          ) : null}
        </section>

        <section className="skillForumSection">
          <h2>Study</h2>
          <p>Community articles for this skill. Publication needs three Intermediate+ approvals or one moderator.</p>
          {articles.length ? (
            <ul className="skillForumArticles">
              {articles.map((article) => {
                const articleId = idOf(article);
                const status = article.Status || article.status || "Pending";
                const pending = String(status).toLowerCase() === "pending";
                const approved = String(status).toLowerCase() === "approved";
                return (
                  <li key={articleId}>
                    <strong>{article.Subject || article.subject}</strong>{" "}
                    <em>({status})</em>
                    <p>{article.Body || article.body}</p>
                    {(isIntermediatePlus || isModerator) && pending ? (
                      <CustomButton type="button" variant="contained" color="primary" onClick={() => approveArticle(articleId)}>
                        Approve
                      </CustomButton>
                    ) : null}
                    {isModerator && (pending || approved) ? (
                      <CustomButton type="button" variant="contained" color="primary" onClick={() => rejectArticle(articleId)}>
                        Reject
                      </CustomButton>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="fieldHint">No articles yet for this skill.</p>
          )}
          {isIntermediatePlus || isModerator ? (
            <form className="skillForumArticleForm" onSubmit={createArticle}>
              <label>
                Subject
                <input
                  value={articleForm.subject}
                  onChange={(event) => setArticleForm({ ...articleForm, subject: event.target.value })}
                  maxLength={100}
                  required
                />
              </label>
              <label>
                Body
                <textarea
                  value={articleForm.body}
                  onChange={(event) => setArticleForm({ ...articleForm, body: event.target.value })}
                  rows={4}
                  required
                />
              </label>
              <label>
                Target level{" "}
                <select
                  value={articleForm.skillLevelID}
                  onChange={(event) => setArticleForm({ ...articleForm, skillLevelID: Number(event.target.value) })}
                >
                  {Object.entries(LEVEL_NAMES).map(([level, name]) => (
                    <option key={level} value={level}>{name}</option>
                  ))}
                </select>
              </label>
              <p className="fieldHint">
                {isIntermediatePlus
                  ? "You can publish study articles for this skill."
                  : "Moderator shortcut: create still requires Intermediate+ verified level on the API."}
              </p>
              <CustomButton type="submit" variant="contained" color="primary" disabled={busy || !isIntermediatePlus}>
                Submit article
              </CustomButton>
            </form>
          ) : (
            <p className="fieldHint">Verified Intermediate or higher is required to author articles.</p>
          )}
        </section>

        <section className="skillForumSection">
          <h2>Test bank and eligibility</h2>
          {eligible ? (
            <>
              <p>
                Type 2 discover: {Number(eligible.approvedCount ?? 0)}/
                {Number(eligible.requiredCount ?? 20)} approved ·{" "}
                {eligible.type2Eligible ? "Ready" : "Not ready"}
                {eligible.type2Eligible ? (
                  <>
                    {" "}
                    <CustomButton type="button" variant="contained" color="primary" disabled={busy} onClick={() => startTest(2)}>
                      Start Type 2
                    </CustomButton>
                  </>
                ) : null}
              </p>
              <p className="fieldHint">
                Renew or re-detect verified level with Type 2 (writes certification when allowed):
              </p>
              {eligible.type2Eligible ? (
                <CustomButton
                  type="button"
                  variant="contained"
                  color="primary"
                  disabled={busy}
                  data-testid="skill-forum-type2-renew"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Run level detection and update your verified skill level? This renews the 2-year clock."
                      )
                    ) {
                      startTest(2, 0, true);
                    }
                  }}
                >
                  Renew / re-detect level (Type 2)
                </CustomButton>
              ) : null}
              <ul>
                {(eligible.levels || []).map((level) => {
                  const required = Number(eligible.requiredCount ?? 20);
                  const approved = Number(level.approvedCount ?? 0);
                  const pending = Number(level.pendingCount ?? 0);
                  const contested = Number(level.contestedCount ?? 0);
                  const ready = !!level.type1Eligible;
                  return (
                    <li key={level.skillLevel}>
                      Type 1 {LEVEL_NAMES[level.skillLevel] || level.name}: {approved}/{required} approved
                      {" · "}
                      <span className={ready ? "skillForumBankReady" : "skillForumBankNotReady"}>
                        {ready ? "Ready" : "Not ready"}
                      </span>
                      {" · "}
                      {pending} pending · {contested} contested
                      {ready ? (
                        <CustomButton
                          type="button"
                          variant="contained"
                          color="primary"
                          disabled={busy}
                          onClick={() => startTest(1, level.skillLevel)}
                        >
                          Start Type 1
                        </CustomButton>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <p className="fieldHint">Loading test eligibility…</p>
          )}
        </section>

        <section className="skillForumSection">
          <h2>Create question</h2>
          <p>Pending questions need three approvals from people who hold this skill.</p>
          {form.alternativeToQuestionID ? (
            <p className="fieldHint">
              Creating as alternate to question #{form.alternativeToQuestionID}.{" "}
              <button
                type="button"
                onClick={() => setForm({ ...form, alternativeToQuestionID: null })}
              >
                Clear
              </button>
            </p>
          ) : null}
          <form onSubmit={createQuestion}>
            <label>
              Level{" "}
              <select value={form.skillLevelID} onChange={(event) => setForm({ ...form, skillLevelID: Number(event.target.value) })}>
                {Object.entries(LEVEL_NAMES).map(([level, name]) => (
                  <option key={level} value={level}>{name}</option>
                ))}
              </select>
            </label>
            <label>
              Question
              <textarea value={form.questionText} onChange={(event) => setForm({ ...form, questionText: event.target.value })} rows={3} required />
            </label>
            {form.answers.map((answer, index) => (
              <div className="skillForumAnswer" key={index}>
                <label>
                  Answer {index + 1}
                  <input value={answer.text} onChange={(event) => updateAnswer(index, { text: event.target.value })} required={index < 2} />
                </label>
                <label>
                  <input type="radio" name="correct-answer" checked={!!answer.correctAnswer} onChange={() => updateAnswer(index, { correctAnswer: true })} />
                  Correct
                </label>
              </div>
            ))}
            <CustomButton type="button" variant="contained" color="primary" onClick={() => setForm({ ...form, answers: [...form.answers, { text: "", correctAnswer: false }] })}>
              Add answer
            </CustomButton>
            <CustomButton type="submit" variant="contained" color="primary" disabled={busy}>
              Submit question
            </CustomButton>
          </form>
        </section>

        <section className="skillForumSection">
          <h2>Questions</h2>
          {questions.length ? (
            <ul className="skillForumQuestions">
              {questions.map((question) => {
                const questionId = idOf(question);
                const pending = isPending(question);
                const approved = isApproved(question);
                const contested = isContested(question);
                const votable = isVotable(question);
                const approveCount = Number(question.ApproveCount ?? question.approveCount ?? 0);
                const disapproveCount = Number(question.DisapproveCount ?? question.disapproveCount ?? 0);
                const altOf = Number(question.AlternativeToQuestionID ?? question.alternativeToQuestionID) || 0;
                return (
                  <li key={questionId} className={questionId === selectedQuestionId ? "selected" : ""}>
                    <button type="button" className="skillForumQuestion" onClick={() => setSelectedQuestionId(questionId)}>
                      <strong>{LEVEL_NAMES[question.SkillLevelID || question.skillLevelID] || "Question"}</strong> —{" "}
                      {question.QuestionText || question.questionText}{" "}
                      <em>({questionStatusLabel(question)})</em>
                      {contested ? <span className="skillForumContestedBadge"> Contested</span> : null}
                      {altOf ? <span className="fieldHint"> · alt of #{altOf}</span> : null}
                      <span className="fieldHint"> · +{approveCount} / −{disapproveCount}</span>
                    </button>
                    {votable ? (
                      <>
                        <CustomButton type="button" variant="contained" color="primary" onClick={() => approveQuestion(questionId)}>
                          Approve
                        </CustomButton>
                        <CustomButton type="button" variant="contained" color="primary" onClick={() => disapproveQuestion(questionId)}>
                          Disapprove
                        </CustomButton>
                      </>
                    ) : null}
                    <CustomButton
                      type="button"
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setForm({
                          ...emptyQuestion(),
                          alternativeToQuestionID: questionId,
                          skillLevelID: Number(question.SkillLevelID || question.skillLevelID) || 1,
                        });
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      Propose alternate
                    </CustomButton>
                    <CustomButton
                      type="button"
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setSelectedQuestionId(questionId);
                        setEditForm({
                          questionText: question.QuestionText || question.questionText || "",
                          explanation: question.Explanation || question.explanation || "",
                          answers: [
                            { text: "", correctAnswer: true },
                            { text: "", correctAnswer: false },
                          ],
                        });
                      }}
                    >
                      Suggest edit
                    </CustomButton>
                    {isModerator && contested ? (
                      <CustomButton type="button" variant="contained" color="primary" onClick={() => restoreQuestion(questionId)}>
                        Restore
                      </CustomButton>
                    ) : null}
                    {isModerator && pending ? (
                      <CustomButton type="button" variant="contained" color="primary" onClick={() => rejectQuestion(questionId, false)}>
                        Reject
                      </CustomButton>
                    ) : null}
                    {isModerator && (approved || contested) ? (
                      <CustomButton type="button" variant="contained" color="primary" onClick={() => rejectQuestion(questionId, true)}>
                        Retract
                      </CustomButton>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p>No questions have been submitted for this skill yet.</p>
          )}
        </section>

        {editForm && selectedQuestionId > 0 ? (
          <section className="skillForumSection">
            <h2>Suggest edit for question #{selectedQuestionId}</h2>
            <form onSubmit={submitEditSuggestion}>
              <label>
                Question
                <textarea
                  value={editForm.questionText}
                  onChange={(e) => setEditForm({ ...editForm, questionText: e.target.value })}
                  rows={3}
                  required
                />
              </label>
              <label>
                Explanation
                <textarea
                  value={editForm.explanation}
                  onChange={(e) => setEditForm({ ...editForm, explanation: e.target.value })}
                  rows={2}
                />
              </label>
              {editForm.answers.map((answer, index) => (
                <div className="skillForumAnswer" key={index}>
                  <label>
                    Answer {index + 1}
                    <input
                      value={answer.text}
                      onChange={(e) => {
                        const answers = editForm.answers.slice();
                        answers[index] = { ...answers[index], text: e.target.value };
                        setEditForm({ ...editForm, answers });
                      }}
                      required={index < 2}
                    />
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="edit-correct-answer"
                      checked={!!answer.correctAnswer}
                      onChange={() => {
                        const answers = editForm.answers.map((a, i) => ({
                          ...a,
                          correctAnswer: i === index,
                        }));
                        setEditForm({ ...editForm, answers });
                      }}
                    />
                    Correct
                  </label>
                </div>
              ))}
              <CustomButton type="submit" variant="contained" color="primary" disabled={busy}>
                Submit suggestion
              </CustomButton>
              <CustomButton type="button" variant="contained" color="primary" onClick={() => setEditForm(null)}>
                Cancel
              </CustomButton>
            </form>
          </section>
        ) : null}

        {selectedQuestionId > 0 && editSuggestions.length ? (
          <section className="skillForumSection">
            <h2>Edit suggestions</h2>
            <ul>
              {editSuggestions.map((s) => {
                const sid = idOf(s);
                const st = Number(s.Status ?? s.status);
                return (
                  <li key={sid}>
                    <strong>{st === 0 ? "Pending" : st === 1 ? "Accepted" : "Rejected"}</strong> —{" "}
                    {s.QuestionText || s.questionText}
                    {st === 0 ? (
                      <>
                        <CustomButton type="button" variant="contained" color="primary" onClick={() => resolveEditSuggestion(sid, true)}>
                          Accept
                        </CustomButton>
                        <CustomButton type="button" variant="contained" color="primary" onClick={() => resolveEditSuggestion(sid, false)}>
                          Reject
                        </CustomButton>
                      </>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        {selectedQuestion && isPending(selectedQuestion) ? (
          <section className="skillForumSection">
            <h2>Discussion</h2>
            <p>{selectedQuestion.QuestionText || selectedQuestion.questionText}</p>
            {discussion.length ? (
              <ul className="skillForumDiscussion">
                {discussion.map((post) => (
                  <li key={idOf(post)}>
                    <strong>{post.PersonName || post.personName || "Community member"}</strong>
                    <span>{post.Message || post.message}</span>
                    {isModerator ? (
                      <CustomButton
                        type="button"
                        variant="contained"
                        color="primary"
                        onClick={() => deleteDiscussion(idOf(post))}
                      >
                        Remove
                      </CustomButton>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p>Start the discussion for this pending question.</p>
            )}
            <form onSubmit={postDiscussion}>
              <label>
                Add to the discussion
                <textarea value={message} onChange={(event) => setMessage(event.target.value)} rows={3} required />
              </label>
              <CustomButton type="submit" variant="contained" color="primary" disabled={busy || !message.trim()}>
                Post discussion
              </CustomButton>
            </form>
          </section>
        ) : null}
      </div>
    </div>
  );
}
